/**
 * Placeholder marketing copy for the public pre-order pages.
 *
 * ALL OF IT IS DEMO TEXT. It exists so the page has the shape of a real
 * product page while the launch copy is being written, and every line here is
 * meant to be replaced before this goes in front of customers. Nothing in it
 * reaches the backend or the order: it is presentation only.
 *
 * Deliberately absent, and not to be filled in here: star ratings, customer
 * reviews and a struck-through MRP. Each of those is a factual claim about
 * price or reception, and an invented one is a problem whatever the intent -
 * so the page shows a comparison price only when the API sends a real one,
 * and has no review section until there are reviews.
 *
 * Keyed by campaign slug, with a fallback for any campaign that has none yet.
 */

const IMG = '/assets/img/preorder';

/*
 * The ambient product loop is switched off on every campaign: `video: null`
 * below, which renders no video element at all. Turned off by decision, not
 * removed - the page still knows how to show one.
 *
 * To bring it back for a campaign: put a short, silent, muted-autoplay mp4
 * under public/assets/video/preorder/ and set
 *   video: { src: '/assets/video/preorder/<name>.mp4', poster: '' }
 * on that entry. Keep it under about 3MB; it is served off the same box as
 * everything else, and this stack has no CDN.
 */

const COMMON_VALUE_PROPS = [
  { icon: 'wallet', title: 'Nothing to pay today', note: 'Reserve now. We ask for payment only once your unit is ready to ship.' },
  { icon: 'bolt', title: 'First batch, first out', note: 'Reservations are dispatched in the order they were placed.' },
  { icon: 'shield', title: '1-year warranty', note: 'Covered against manufacturing defects from the day it is delivered.' },
  { icon: 'truck', title: 'Free delivery', note: 'Shipped free anywhere we deliver in India, tracked end to end.' },
];

const COMMON_FAQS = [
  {
    q: 'What does a pre-order actually commit me to?',
    a: 'It holds a unit from the first production batch in your name. No money changes hands today, and you can cancel at any point before you pay, with nothing owed.',
  },
  {
    q: 'When will I be asked to pay?',
    a: 'When your unit is built and ready to dispatch. We email you a payment link, and the device ships as soon as that payment clears.',
  },
  {
    q: 'What if the dispatch date moves?',
    a: 'We will tell you. The window shown on this page is an estimate, not a guaranteed delivery date, and if it moves materially you can cancel for a full refund.',
  },
  {
    q: 'Can I change my mind after it ships?',
    a: 'Yes. Our standard returns window applies from the day it reaches you, on the terms set out in the refund policy linked above.',
  },
  {
    q: 'Do I need an Emed4u account?',
    a: 'Yes, so the reservation has somewhere to live and you can track it. Creating one takes an email address and a code we send you.',
  },
];

export const DEMO_CONTENT = {
  /*
   * APPROVED COPY. Unlike the rest of this file, the Koti Ring entry is not
   * placeholder text: it is transcribed from "Koti Ring Website Copy -
   * Customer Facing Content" (source content/pages/koti-ring.json), and the
   * wording is deliberate. The hedges - "supported", "estimates",
   * "wellness" - are compliance language for a product that is explicitly
   * not a medical device, so do not tighten them into stronger claims.
   *
   * Three specification rows from that document are deliberately absent:
   * materials/weight, battery duration and the exact box contents each
   * carried an internal note ("supplier confirmation pending", "do not
   * claim titanium or under 4 g") rather than a value fit to publish. They
   * belong here once the supplier confirms them, not before.
   */
  'koti-ring': {
    /*
     * The page's own headline, which is longer than the product's name.
     *
     * The approved document heads the page "Koti Ring: Everyday Wellness
     * Smart Ring" while still calling the product "Koti Ring" in the
     * selector table and on the reserve button - so this is a headline,
     * not a rename. The campaign record keeps the short name, which is
     * what the picker, the pinned bar, the package contents and every
     * order go on using.
     */
    headline: 'Koti Ring: Everyday Wellness Smart Ring',
    // Used when the campaign record carries no photographs of its own.
    gallery: [`${IMG}/koti-ring-1.jpg`, `${IMG}/koti-ring-2.jpg`, `${IMG}/koti-ring-3.jpg`, `${IMG}/koti-ring-4.jpg`],
    tagline: 'Notice the patterns behind your day. Track heart-rate, sleep and activity trends '
      + 'in a screen-free ring, with your information brought together in Emed4u.',
    /* Sits under the price, where the old page had nothing. All three
       parts together, as the approved document sets them out: two
       compliance statements and what the reservation costs today. */
    productClass: 'General wellness product · Not a medical device · ₹0 reservation today',
    valueProps: COMMON_VALUE_PROPS,

    /*
     * Sections 2 and 3. The short product description is the standfirst
     * over the four benefits, which is how the approved document sets them
     * out, and it keeps the copy together rather than splitting the lead
     * sentence away from what it introduces.
     */
    keyBenefits: {
      /*
       * Heading only. The paragraph that follows this line in the approved
       * document is the product's short description, which belongs on the
       * campaign record and is already shown in Product details further up
       * - printing it again here would just repeat it a screen later.
       */
      title: 'Better awareness starts with your own baseline',
      items: [
        {
          icon: 'insights',
          title: 'Understand your sleep rhythm',
          note: 'Review your sleep-related wellness patterns over time.',
        },
        {
          icon: 'bolt',
          title: 'Follow your daily trends',
          note: 'See heart-rate, activity and supported respiratory wellness estimates.',
        },
        {
          // No ring glyph in the set; the box stands for the thing itself.
          icon: 'box',
          title: 'Stay screen-free',
          note: 'A discreet ring format that does not require another watch display.',
        },
        {
          icon: 'shield',
          title: 'Connect your information',
          note: 'Review supported Ring data in the Emed4u experience.',
        },
      ],
    },

    /* Section 4. The approved copy names ring-lifestyle, ring-case and
       ring-sensors; these are the shots we hold that match those briefs. */
    features: [
      {
        title: 'Look beyond a single night',
        body: 'See how sleep patterns change over time, rather than relying on one isolated score.',
        image: `${IMG}/koti-ring-2.jpg`,
      },
      {
        title: 'Understand your wellness baseline',
        body: 'Follow heart-rate, activity and supported respiratory-rate and wellness SpO\u2082 '
          + 'estimates in the Emed4u app. Ring measurements are wellness estimates and cannot '
          + 'diagnose disease.',
        image: `${IMG}/koti-ring-3.jpg`,
      },
      {
        title: 'A simple way to start',
        body: 'Wear the discreet Ring to follow everyday patterns. When you also want dedicated '
          + 'blood-pressure readings, choose Koti Duo.',
        image: `${IMG}/koti-ring-4.jpg`,
      },
    ],

    /* Section 5. Its own heading and standfirst, because this part is about
       the platform rather than the device. */
    platform: {
      title: 'Your Ring is a start. Emed4u connects the rest.',
      note: 'Build on your wellness trends with a more connected health record.',
      items: [
        { icon: 'insights', title: 'Wellness dashboard', note: 'See supported ring measurements in a clear, organized app.' },
        { icon: 'shield', title: 'Health Angel', note: 'Share selected records where user-controlled sharing is available.' },
        { icon: 'truck', title: 'Care access', note: 'See available Emed4u audio/video consultations.' },
        { icon: 'wallet', title: 'Upgrade to Duo', note: 'Add cuff-measured BP and supported ECG for just \u20b97,000 more than Ring alone.' },
      ],
    },

    /*
     * Section 6, all seven rows, verbatim.
     *
     * Three values end with an instruction to our own team rather than
     * information for the reader - "confirm exact SKU/firmware", "do not
     * claim titanium or under 4 g", "confirm final packing". They are
     * printed anyway, at the owner's explicit direction, so the page is a
     * faithful copy of the approved document. Strip them before this goes
     * in front of customers.
     */
    /*
     * Unchanged by the copy rewrite: the supplied marketing panels and the
     * ambient loop are page furniture the approved document does not cover,
     * and dropping them would remove working sections it never asked about.
     */
    showcase: [
      {
        image: `${IMG}/koti-ring-app-1.jpg`,
        alt: 'Koti Ring beside the Emed4u app: heart-rate tracking, SpO2, sleep insights and '
          + 'activity, with a month of readings charted on the app home screen.',
      },
      {
        image: `${IMG}/koti-ring-app-2.jpg`,
        alt: 'Koti Ring beside the Emed4u app: respiratory-rate monitoring, trend analysis and '
          + 'high and low alerts, with breaths per minute charted over a month.',
      },
    ],
    video: null,

    /* The approved copy has no equivalent of this list, so the section is
       left out rather than filled with wording nobody signed off. */
    builtDifferent: [],

    /* Section 7, replacing the shared set: these answer the same ground and
       were written for this product. */
    faqs: [
      {
        q: 'Is Koti Ring a medical device?',
        a: 'No. Koti Ring is intended for general wellness tracking only. It does not diagnose, '
          + 'treat, or monitor a medical condition.',
      },
      {
        q: 'Can Koti Ring measure blood pressure?',
        a: 'No. For a BP measurement choose the Koti BP + ECG Cuff or Koti Duo.',
      },
      {
        q: 'What does the Ring track?',
        a: 'Subject to final SKU and app validation, it can display heart-rate, activity, sleep, '
          + 'breathing-rate and wellness SpO\u2082 trends.',
      },
      {
        q: 'Would Koti Duo be better for me?',
        a: 'If you want everyday wellness trends plus separate upper-arm BP measurements, Koti Duo '
          + 'includes Ring and Cuff together for \u20b914,999, saving \u20b92,999.',
      },
      {
        q: 'Is a payment required today?',
        a: 'The proposed pre-order process requests \u20b90 today. Payment is requested before '
          + 'dispatch, subject to the final reservation and cancellation terms.',
      },
      {
        q: 'Can I share my results with a family member or doctor?',
        a: 'Emed4u is developing permission-based sharing through Health Angel. Availability and '
          + 'access controls will be confirmed for the launch release.',
      },
      {
        q: 'Does the Emed4u app require a subscription?',
        a: 'Core Emed4u app access is planned to be included with the device purchase. Optional '
          + 'consultations and additional professional services may have separate fees.',
      },
    ],

    /* Section 8, which is the one part of this page that is not marketing.
       Shown last and always, not folded into an FAQ someone can leave shut.
       The document's closing sentence about confirming metrics for the
       shipped TK30 is an instruction to the team, not to the reader, so it
       is not repeated here. */

    /*
     * Section 1's two calls to action.
     *
     * {price} is substituted at render time from the campaign record, not
     * written out here: the figures in the document are today's approved
     * prices, and a button that keeps claiming an old one after a price
     * change is the kind of mistake nobody notices until a customer does.
     * `name` is the short form the document uses on the button, which is
     * not always the product's full title.
     */
    cta: {
      name: 'Koti Ring',
      primary: 'Reserve {name} — {price}',
      secondary: { label: 'Get Ring + Cuff for {price}', slug: 'koti-duo' },
    },

    /* Section 9. */
    seo: {
      title: 'Koti Smart Ring for Sleep & Wellness Tracking | Emed4u',
      description: 'Discover Koti Ring at \u20b97,999 for heart-rate, sleep and activity wellness '
        + 'trends in Emed4u. Add the Koti BP/ECG Cuff with Koti Duo for \u20b914,999.',
      keywords: 'smart ring India, sleep tracking ring India, wellness ring, heart rate ring, smart ring Emed4u',
      ogTitle: 'Koti Ring \u2014 Notice the Patterns Behind Your Day',
      ogDescription: 'A simple, discreet smart ring for everyday wellness. \u20b97,999.',
      ogImage: `${IMG}/koti-ring-1.jpg`,
    },
  },

  /*
   * APPROVED COPY, from "Koti BP + ECG Cuff Website Copy - Customer Facing
   * Content" (source content/pages/koti-bp-ecg-cuff.json).
   *
   * Keyed by the campaign's slug, which is now koti-bp-ecg-cuff. It was
   * koti-cough until the rename; PREORDER_SLUG_ALIASES in PreorderLanding
   * keeps that old address working for anyone who bookmarked or shared it.
   *
   * The image filenames are still koti-cough-*.jpg. They are internal, the
   * photographs are correct, and renaming files for tidiness would mean
   * re-uploading every one of them for no reader-visible gain.
   *
   * It also settles the photography question that has been open on this
   * campaign: the supplied shots were always of a blood-pressure cuff
   * because the product is one. Nothing here needs reshooting.
   *
   * All seven specification rows, verbatim. Two of them end with a note
   * to our own team - "confirm exact Koti accessory", "to be confirmed for
   * shipped configuration" - and are printed anyway, at the owner's
   * direction, so the page is a faithful copy of the document. Strip them
   * before this goes in front of customers. The US regulatory row is kept
   * in full deliberately: it is a statement to the reader, and the part
   * saying this is not India authorization is the point of it.
   */
  'koti-bp-ecg-cuff': {
    /*
     * The page's headline, longer than the product's name - the document
     * heads the page "Koti BP + ECG Cuff: Upper-Arm BP Monitor with ECG"
     * while still calling the product "Koti BP + ECG Cuff" in the selector
     * table. The record keeps the short name, which is what the picker,
     * the pinned bar, the package contents and every order use.
     */
    headline: 'Koti BP + ECG Cuff: Upper-Arm BP Monitor with ECG',
    gallery: [
      `${IMG}/koti-cough-1.jpg`,
      `${IMG}/koti-cough-2.jpg`,
      `${IMG}/koti-cough-3.jpg`,
      `${IMG}/koti-cough-4.jpg`,
      `${IMG}/koti-cough-5.jpg`,
      `${IMG}/koti-cough-6.jpg`,
      `${IMG}/koti-cough-7.jpg`,
    ],
    tagline: 'Your readings, easier to follow. Measure blood pressure with an upper-arm cuff, '
      + 'keep a dated history and connect supported records in the Emed4u app.',
    productClass: 'BP measured when you use the cuff \u00b7 ECG feature subject to '
      + 'authorization \u00b7 \u20b90 reservation today',
    valueProps: COMMON_VALUE_PROPS,

    keyBenefits: {
      title: 'A reading matters. A history brings context.',
      items: [
        {
          icon: 'insights',
          title: 'Use a dedicated upper-arm cuff',
          note: 'Take BP readings at the times advised by your healthcare professional.',
        },
        {
          icon: 'box',
          title: 'Keep a record',
          note: 'Store supported blood-pressure measurements by date.',
        },
        {
          icon: 'bolt',
          title: 'Supported ECG records',
          note: 'EMED4U BP + ECG hardware also supports single-lead ECG recording.',
        },
        {
          icon: 'wallet',
          title: 'Add everyday context',
          note: 'Koti Duo adds the Ring\u2019s wellness trends, saving \u20b92,999 against separate purchases.',
        },
      ],
    },

    features: [
      {
        title: 'Measure BP at home',
        body: 'Use the correctly fitted upper-arm cuff and follow the supplied instructions for '
          + 'a seated measurement. For consistent readings, follow device instructions and the '
          + 'schedule agreed with your clinician.',
        image: `${IMG}/koti-cough-1.jpg`,
      },
      {
        title: 'Keep your measurements organized',
        body: 'Review systolic and diastolic values over days and weeks where Emed4u integration '
          + 'is enabled.',
        image: `${IMG}/koti-cough-2.jpg`,
      },
      {
        title: 'Share the record, not just the number',
        body: 'Emed4u aims to make selected records accessible to people you authorize, through '
          + 'Health Angel and available clinician consultation services.',
        image: `${IMG}/koti-cough-app-1.jpg`,
      },
    ],

    showcase: [
      {
        image: `${IMG}/koti-cough-app-1.jpg`,
        alt: 'Koti BP + ECG cuff beside the Emed4u app: one-click blood-pressure measurement '
          + 'over Bluetooth, with systolic and diastolic readings charted by day.',
      },
      {
        image: `${IMG}/koti-cough-app-2.jpg`,
        alt: 'Koti BP cuff beside the Emed4u app: single-lead ECG recording, rhythm analysis '
          + 'and dated ECG traces listed in the app.',
      },
    ],
    video: null,

    platform: {
      title: 'Connected care begins with a useful reading.',
      note: 'Combine readings with wellness context and share them with people you trust.',
      items: [
        { icon: 'insights', title: 'Track your BP history', note: 'Organize supported measurements instead of using scattered notes.' },
        { icon: 'bolt', title: 'Optional ECG recording', note: 'EMED4U BP + ECG hardware also supports single-lead ECG recording.' },
        { icon: 'shield', title: 'Health Angel', note: 'Permission-based access to selected records, if enabled at launch.' },
        { icon: 'wallet', title: 'Koti Duo upgrade', note: 'Ring and Cuff together for \u20b914,999, saving \u20b92,999.' },
      ],
    },

    /* The approved document lists no box contents for this device on its
       own, so the section is left out rather than guessed at. */
    builtDifferent: [],

    faqs: [
      {
        q: 'Does the cuff measure BP all day automatically?',
        a: 'No. You take a blood-pressure measurement when you use the cuff. The Ring does not '
          + 'replace cuff measurements.',
      },
      {
        q: 'Is the Koti cuff FDA approved?',
        a: 'The underlying Viatom BP2 model has US FDA 510(k) clearance K193348, which is '
          + 'different from FDA approval. The cleared US ECG indication is prescription-only. '
          + 'Koti labeling and India approvals must be verified separately.',
      },
      {
        q: 'Can the ECG feature diagnose a heart condition?',
        a: 'No. ECG recordings should not be used to independently diagnose or rule out a heart '
          + 'condition. Feature availability depends on local authorization and professional advice.',
      },
      {
        q: 'Why buy the Koti Duo package?',
        a: 'Koti Duo pairs dedicated BP measurements with the Koti Ring\u2019s general wellness '
          + 'trends. At \u20b914,999 you save \u20b92,999 compared with purchasing the two devices separately.',
      },
      {
        q: 'Is a payment required today?',
        a: 'The proposed pre-order process requests \u20b90 today. Payment is requested before '
          + 'dispatch, subject to the final reservation and cancellation terms.',
      },
      {
        q: 'Can I share my results with a family member or doctor?',
        a: 'Emed4u is developing permission-based sharing through Health Angel. Availability and '
          + 'access controls will be confirmed for the launch release.',
      },
      {
        q: 'Does the Emed4u app require a subscription?',
        a: 'Core Emed4u app access is planned to be included with the device purchase. Optional '
          + 'consultations and additional professional services may have separate fees.',
      },
    ],


    /*
     * Section 1's two calls to action. {price} is substituted from the
     * campaign record at render time, never written out here, so a button
     * cannot go on advertising a price the page no longer sells at.
     * `name` is the short form the document puts on the button, which is
     * not the product's full title.
     */
    cta: {
      name: 'BP + ECG Cuff',
      primary: 'Reserve {name} \u2014 {price}',
      secondary: { label: 'Get Ring + Cuff for {price}', slug: 'koti-duo' },
    },

    seo: {
      title: 'Koti BP + ECG Cuff | Home Blood Pressure Monitor | Emed4u',
      description: 'Take upper-arm BP readings with Koti BP + ECG Cuff and see supported records '
        + 'in Emed4u. \u20b99,999. Combine with Koti Ring in Duo at \u20b914,999.',
      keywords: 'home blood pressure monitor India, BP monitor with ECG, upper arm digital BP cuff, '
        + 'connected BP monitor Emed4u',
      ogTitle: 'Koti BP + ECG Cuff \u2014 Your Readings, Easier to Follow',
      ogDescription: 'Dedicated blood-pressure readings. A clearer history in Emed4u. \u20b99,999.',
      ogImage: `${IMG}/koti-cough-1.jpg`,
    },
  },

  /*
   * APPROVED COPY, from "Koti Duo Website Copy - Customer Facing Content"
   * (source content/pages/koti-duo.json).
   *
   * The package's own case now comes from `keyBenefits` like the two single
   * devices, rather than from the bespoke `whyPackage` block this campaign
   * used to carry - the approved document writes one set of benefits, not
   * two, so there is nothing left for the old section to say.
   */
  'koti-duo': {
    /*
     * The package's own photography, and nothing else. Both devices
     * together first, then the two retail boxes, then the cuff's box on
     * its own. Nothing from the single-device pages is mixed in: a package
     * that opens on a photograph of one device reads as that device.
     *
     * The rail that lists them scrolls on its own - it is pinned beside
     * the photograph, and a thumbnail past the fold would be out of reach.
     */
    gallery: [
      `${IMG}/koti-duo-1.jpg`,
      `${IMG}/koti-duo-2.jpg`,
      `${IMG}/koti-duo-3.jpg`,
      `${IMG}/koti-duo-4.jpg`,
      `${IMG}/koti-duo-5.jpg`,
    ],
    tagline: 'Your health, in a fuller picture. Wellness trends from your Ring. Dedicated BP '
      + 'readings from your Cuff. A more connected way to see your information with Emed4u.',
    productClass: 'Free core app access \u00b7 12-month limited warranty \u00b7 '
      + '\u20b90 reservation today',
    valueProps: COMMON_VALUE_PROPS,
    /* Superseded by keyBenefits below; kept empty so the old section drops
       out rather than printing a second, unapproved set of reasons. */
    whyPackage: [],

    keyBenefits: {
      title: 'Two devices. One connected health journey.',
      items: [
        {
          icon: 'insights',
          title: 'Know your everyday patterns',
          note: 'Follow personal wellness trends over time through Koti Ring.',
        },
        {
          icon: 'bolt',
          title: 'Measure with a dedicated cuff',
          note: 'Record upper-arm blood-pressure readings at home when you use the device.',
        },
        {
          icon: 'shield',
          title: 'Share on your terms',
          note: 'Use supported Health Angel controls to share selected records with family or a clinician.',
        },
        {
          icon: 'wallet',
          title: 'Get more for your money',
          note: '\u20b914,999 for both devices, saving \u20b92,999 versus buying them separately.',
        },
      ],
    },

    features: [
      {
        title: 'Trends throughout the day',
        body: 'Koti Ring captures general wellness information such as sleep, heart-rate and '
          + 'activity trends. It is a wellness device, not a medical instrument.',
        image: `${IMG}/koti-ring-2.jpg`,
      },
      {
        title: 'A reading you can keep',
        body: 'Use the Koti BP + ECG Cuff for individual upper-arm BP measurements. Supported ECG '
          + 'records may also be available, depending on local authorization and software '
          + 'configuration. The Ring does not measure blood pressure.',
        image: `${IMG}/koti-cough-1.jpg`,
      },
      {
        title: 'A more connected record',
        body: 'Emed4u brings together supported measurements and wellness trends. Health Angel, '
          + 'consultations, laboratory information and AI summaries are part of the platform '
          + 'roadmap; launch availability will be confirmed.',
        image: `${IMG}/koti-duo-1.jpg`,
      },
    ],

    showcase: [
      {
        image: `${IMG}/koti-ring-app-1.jpg`,
        alt: 'Koti Ring beside the Emed4u app: heart-rate tracking, SpO2, sleep insights and '
          + 'activity, with a month of readings charted on the app home screen.',
      },
      {
        image: `${IMG}/koti-ring-app-2.jpg`,
        alt: 'Koti Ring beside the Emed4u app: respiratory-rate monitoring, trend analysis and '
          + 'high and low alerts, with breaths per minute charted over a month.',
      },
      {
        image: `${IMG}/koti-cough-app-1.jpg`,
        alt: 'Koti BP + ECG cuff beside the Emed4u app: one-click blood-pressure measurement '
          + 'over Bluetooth, with systolic and diastolic readings charted by day.',
      },
      {
        image: `${IMG}/koti-cough-app-2.jpg`,
        alt: 'Koti BP cuff beside the Emed4u app: single-lead ECG recording, rhythm analysis '
          + 'and dated ECG traces listed in the app.',
      },
    ],
    video: null,

    platform: {
      title: 'The value is not only in the devices.',
      note: 'Emed4u is designed to connect the data, the people and the care around you.',
      items: [
        { icon: 'insights', title: 'Your Emed4u record', note: 'Review supported wellness and BP information in one timeline.' },
        { icon: 'shield', title: 'Health Angel', note: 'Permission-based sharing with a family member or healthcare professional.' },
        { icon: 'truck', title: 'Consultation access', note: 'Find available audio and video consultations through Emed4u.' },
        { icon: 'box', title: 'The broader picture', note: 'Planned body measurements, lab records and AI-assisted summaries, subject to launch availability.' },
      ],
    },

    builtDifferent: [],

    faqs: [
      {
        q: 'Why choose Koti Duo instead of the Ring alone?',
        a: 'Koti Ring follows everyday wellness trends. Koti Duo also includes a dedicated '
          + 'upper-arm BP and ECG cuff, letting you keep supported information in one Emed4u account.',
      },
      {
        q: 'Does the Ring measure blood pressure?',
        a: 'No. Koti Ring is a general wellness wearable and does not measure blood pressure. '
          + 'Blood pressure is measured separately using the Koti Cuff.',
      },
      {
        q: 'Does using both devices make each sensor more accurate?',
        a: 'No. The devices provide different measurements. Putting them together can provide '
          + 'more context but does not itself improve their measurement accuracy.',
      },
      {
        q: 'What is the Duo saving?',
        a: 'The proposed standalone prices are \u20b97,999 for Koti Ring and \u20b99,999 for the Cuff, '
          + 'totalling \u20b917,998. Koti Duo costs \u20b914,999, saving \u20b92,999.',
      },
      {
        q: 'Is a payment required today?',
        a: 'The proposed pre-order process requests \u20b90 today. Payment is requested before '
          + 'dispatch, subject to the final reservation and cancellation terms.',
      },
      {
        q: 'Can I share my results with a family member or doctor?',
        a: 'Emed4u is developing permission-based sharing through Health Angel. Availability and '
          + 'access controls will be confirmed for the launch release.',
      },
      {
        q: 'Does the Emed4u app require a subscription?',
        a: 'Core Emed4u app access is planned to be included with the device purchase. Optional '
          + 'consultations and additional professional services may have separate fees.',
      },
    ],


    /*
     * Section 1's two calls to action.
     *
     * The secondary one is not a link to another campaign, as it is on the
     * two single devices - it points at #how-it-works, which is this
     * page's own feature panels. That is the section that answers "how the
     * Duo works": one panel per device and one for the record they share.
     *
     * No `headline` here. The document heads this page "Koti Duo: Smart
     * Ring + BP & ECG Cuff", which is already the campaign's title, so the
     * heading follows the record and a later rename carries through
     * instead of being contradicted by a headline frozen in the copy.
     */
    cta: {
      name: 'Koti Duo',
      primary: 'Reserve {name} \u2014 {price}',
      secondary: { label: 'How the Duo works', anchor: 'how-it-works' },
    },

    seo: {
      title: 'Koti Duo Smart Ring + BP & ECG Cuff | Emed4u India',
      description: 'Koti Duo brings a wellness smart ring and BP + ECG cuff together for '
        + '\u20b914,999. View trends and readings in Emed4u. Save \u20b92,999 versus buying separately.',
      keywords: 'smart ring and BP monitor bundle India, connected health monitor, '
        + 'smart ring with ECG cuff, Emed4u Koti Duo',
      ogTitle: 'Koti Duo \u2014 Your Health, in a Fuller Picture',
      ogDescription: 'Two complementary devices. One Emed4u experience. Koti Duo \u20b914,999.',
      ogImage: `${IMG}/koti-duo-1.jpg`,
    },
  },
};

const FALLBACK = {
  gallery: [],
  showcase: [],
  video: null,
  keyBenefits: null,
  platform: null,
  productClass: '',
  seo: null,
  whyPackage: [],
  tagline: '',
  valueProps: COMMON_VALUE_PROPS,
  features: [],
  builtDifferent: [],
  faqs: COMMON_FAQS,
};

/** Never null: a campaign with no copy of its own still gets the shared parts. */
export const demoFor = (slug) => DEMO_CONTENT[slug] || FALLBACK;
